/**
 * Inspector for **AI · Predict metadata** (`ai.predict_metadata`, ADR-0039).
 *
 * A fields table — each row is a column the node adds: its name, what the
 * model should predict, examples of good values, a type (text / date / number
 * / picklist with allowed values) and whether it is required — then the model
 * (the platform model or a Foundry connection you may use), the limits, and a
 * Test that runs the node over the first three upstream documents through the
 * run's own code path.
 *
 * Supporting inputs add one input port each (in-1 …): reference tables sent to
 * the model, named "Input 1", "Input 2", … Each field picks the inputs it uses
 * and its prompt can name them. A field can be bound to a column of one
 * ("Must match"); a table marked hierarchical makes its bound fields one
 * combination that must be a row of it — the server checks both.
 */

import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  MultiSelect,
  NumberInput,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  Textarea,
  TextInput,
} from "@mantine/core";
import {
  IconArrowDown,
  IconArrowUp,
  IconPlayerPlay,
  IconPlus,
  IconTrash,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import {
  AdvisoryBanner,
  ChatModelPicker,
  PLATFORM_CHOICE_ID,
  defaultChoiceWithPlatform,
  useChatModels,
  type ChatModelChoice,
} from "@/features/solutions/shared";
import { previewPredictMetadata, type PredictPreviewOut } from "@/shared/api/projects";
import { toastApiError } from "@/shared/ui/toast";
import {
  MAX_SUPPORTING_INPUTS,
  supportingInputs,
  supportingLabel,
  type SupportingInput,
} from "./nodeRegistry";

interface FieldRow {
  name: string;
  prompt: string;
  examples: string;
  type: string;
  values: string;
  required: boolean;
  /** Supporting input (1-based) whose column the value must come from; 0 = none. */
  matchInput: number;
  matchColumn: string;
  /** Supporting inputs (1-based) this column's prediction may use. */
  inputs: number[];
}

const NAME_RE = /^[A-Za-z][A-Za-z0-9_-]{0,63}$/;

const TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "date", label: "Date" },
  { value: "number", label: "Number" },
  { value: "picklist", label: "Picklist" },
];

const STATUS_COLOR: Record<string, string> = {
  ok: "green",
  partial: "yellow",
  failed: "red",
  skipped: "gray",
};

/** Mantine badges ellipsise their label by default — never here. */
const BADGE_STYLES = {
  root: { textTransform: "none" as const, maxWidth: "none", height: "auto" },
  label: { overflow: "visible", textOverflow: "clip", whiteSpace: "nowrap" as const },
};

function allInputs(count: number): number[] {
  return Array.from({ length: count }, (_, i) => i + 1);
}

function fieldsOf(config: Record<string, unknown>, supporting: number): FieldRow[] {
  const raw = Array.isArray(config.fields) ? (config.fields as Partial<FieldRow>[]) : [];
  return raw.map((f) => ({
    // No list yet = every input, the same default the server applies.
    inputs: Array.isArray(f.inputs)
      ? f.inputs.map(Number).filter((n) => n >= 1 && n <= supporting)
      : allInputs(supporting),
    name: String(f.name ?? ""),
    prompt: String(f.prompt ?? ""),
    examples: String(f.examples ?? ""),
    type: String(f.type ?? "text"),
    values: String(f.values ?? ""),
    required: Boolean(f.required),
    matchInput: Number(f.matchInput ?? 0) || 0,
    matchColumn: String(f.matchColumn ?? ""),
  }));
}

function matchError(f: FieldRow, supporting: number): string | null {
  if (!f.matchInput) return null;
  if (f.matchInput > supporting) return "That input was removed";
  return f.matchColumn ? null : "Pick the column";
}

function fieldError(rows: FieldRow[], i: number): string | null {
  const name = rows[i]?.name.trim() ?? "";
  if (!name) return "Name the column";
  if (!NAME_RE.test(name)) return "Letters, digits, _ and - — start with a letter";
  if (rows.some((r, j) => j !== i && r.name.trim().toLowerCase() === name.toLowerCase()))
    return "Used twice";
  return null;
}

function documentLabel(row: Record<string, unknown>): string {
  const file = row._file;
  if (file && typeof file === "object" && "name" in file) return String(file.name ?? "");
  return String(row.name__v ?? row.filename__v ?? row.id ?? row.name ?? "—");
}

function cell(value: unknown): string {
  if (value === null || value === undefined) return "—";
  return typeof value === "object" ? JSON.stringify(value) : String(value);
}

export function PredictMetadataConfig({
  nodeId,
  config,
  editable,
  onChange,
  inputColumns,
  pipelineId,
  getGraph,
}: {
  nodeId: string;
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  /** Upstream columns per input port (`in-0`, `in-1`, …). */
  inputColumns: Record<string, string[]>;
  pipelineId: string;
  /** The on-canvas graph, for Test. */
  getGraph: () => Record<string, unknown>;
}) {
  const getToken = useAccessToken();
  const supporting = supportingInputs(config);
  const fields = fieldsOf(config, supporting.length);
  const { options, platform, error } = useChatModels(true);
  const [preview, setPreview] = useState<PredictPreviewOut | null>(null);
  const [testing, setTesting] = useState(false);

  const choice: ChatModelChoice = {
    connectionId: String(config.connectionId ?? "") || PLATFORM_CHOICE_ID,
    deployment: String(config.deployment ?? "") || null,
  };

  // Settle the stored choice once the picklist is known (e.g. a default node
  // gets the platform deployment's name).
  useEffect(() => {
    if (!options || !editable) return;
    const next = defaultChoiceWithPlatform(options, platform, choice);
    if (next.connectionId !== choice.connectionId)
      onChange("connectionId", next.connectionId ?? "");
    if (next.deployment !== choice.deployment) onChange("deployment", next.deployment ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps -- settle once per picklist load
  }, [options, platform]);

  const setFields = (next: FieldRow[]) => onChange("fields", next);
  const setField = (i: number, patch: Partial<FieldRow>) =>
    setFields(fields.map((f, j) => (j === i ? { ...f, ...patch } : f)));
  const move = (i: number, by: number) => {
    const j = i + by;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j] as FieldRow, next[i] as FieldRow];
    setFields(next);
  };

  const setSupporting = (next: SupportingInput[]) => onChange("supporting", next);
  const setInput = (i: number, patch: Partial<SupportingInput>) =>
    setSupporting(supporting.map((s, j) => (j === i ? { ...s, ...patch } : s)));
  // Only the last input can go: removing one in the middle would silently
  // move every wire below it to a different table.
  const removeLastInput = () => {
    const gone = supporting.length;
    setSupporting(supporting.slice(0, -1));
    setFields(
      fields.map((f) => ({
        ...f,
        inputs: f.inputs.filter((n) => n !== gone),
        ...(f.matchInput === gone ? { matchInput: 0, matchColumn: "" } : {}),
      })),
    );
  };
  // A new input is offered to every column; untick it where it doesn't apply.
  const addInput = () => {
    const added = supporting.length + 1;
    setSupporting([...supporting, { label: "", hierarchical: false }]);
    setFields(fields.map((f) => ({ ...f, inputs: [...f.inputs, added] })));
  };
  const tableOptions = supporting.map((s, i) => ({
    value: String(i + 1),
    label: supportingLabel(s, i + 1),
  }));
  const inputOptions = [{ value: "0", label: "— any value" }, ...tableOptions];

  const invalid =
    fields.length === 0 ||
    fields.some((f, i) => fieldError(fields, i) !== null || matchError(f, supporting.length));

  const test = async () => {
    setTesting(true);
    try {
      const token = await getToken();
      setPreview(await previewPredictMetadata(token, pipelineId, getGraph(), nodeId));
    } catch (err) {
      toastApiError("Test failed", err);
    } finally {
      setTesting(false);
    }
  };

  const names = fields.map((f) => f.name.trim()).filter(Boolean);

  return (
    <Stack gap="sm">
      <AdvisoryBanner />
      <Text size="xs" c="dimmed">
        Each row is a column this node adds. Before a Load to Veeva, put an Approval node between
        this node and the load — the run is refused otherwise.
      </Text>

      <Text size="sm" fw={600}>
        Supporting inputs
      </Text>
      <Text size="xs" c="dimmed">
        Reference tables, such as your document types, subtypes and classifications. Each one adds
        an input port under the Documents port. In a column&apos;s prompt, refer to them as Input 1,
        Input 2, … or by their label.
      </Text>
      {supporting.map((s, i) => {
        const cols = inputColumns[`in-${i + 1}`] ?? [];
        return (
          <Paper key={i} withBorder p="xs" radius="sm">
            <Stack gap={6}>
              <Group gap={4} wrap="nowrap" align="flex-end">
                <TextInput
                  label={`Input ${i + 1}`}
                  placeholder="e.g. Veeva document types"
                  description="Optional label: what this table is, for the AI"
                  value={s.label}
                  maxLength={80}
                  disabled={!editable}
                  style={{ flex: 1 }}
                  onChange={(e) => setInput(i, { label: e.currentTarget.value })}
                />
                {i === supporting.length - 1 && (
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label="Remove supporting input"
                    disabled={!editable}
                    mb={6}
                    onClick={removeLastInput}
                  >
                    <IconTrash size={14} />
                  </ActionIcon>
                )}
              </Group>
              <Switch
                label="Hierarchical"
                description="Each row is one valid combination, with the parent column on the left (e.g. Type → Subtype → Classification). Fields that must match this table must together form one of its rows."
                checked={s.hierarchical}
                disabled={!editable}
                onChange={(e) => setInput(i, { hierarchical: e.currentTarget.checked })}
              />
              <Text size="xs" c={cols.length ? "dimmed" : "orange"}>
                {cols.length
                  ? `Columns: ${cols.join(" → ")}`
                  : "Not connected yet. Wire a table into this port."}
              </Text>
            </Stack>
          </Paper>
        );
      })}
      <Button
        variant="light"
        size="xs"
        leftSection={<IconPlus size={14} />}
        disabled={!editable || supporting.length >= MAX_SUPPORTING_INPUTS}
        onClick={addInput}
      >
        Add supporting input
      </Button>

      <Text size="sm" fw={600}>
        Fields
      </Text>

      {fields.map((f, i) => {
        const err = fieldError(fields, i);
        return (
          <Paper key={i} withBorder p="xs" radius="sm">
            <Stack gap={6}>
              <Group gap={4} wrap="nowrap" align="flex-start">
                <TextInput
                  label="Column"
                  placeholder="column_name"
                  value={f.name}
                  error={err}
                  disabled={!editable}
                  style={{ flex: 1 }}
                  styles={{ input: { fontFamily: "var(--mantine-font-family-monospace)" } }}
                  onChange={(e) => setField(i, { name: e.currentTarget.value })}
                />
                <Select
                  label="Type"
                  data={TYPE_OPTIONS}
                  value={f.type}
                  allowDeselect={false}
                  disabled={!editable}
                  w={110}
                  onChange={(v) => setField(i, { type: v ?? "text" })}
                />
                <Stack gap={0} pt={22}>
                  <Group gap={0} wrap="nowrap">
                    <ActionIcon
                      variant="subtle"
                      aria-label="Move up"
                      disabled={!editable || i === 0}
                      onClick={() => move(i, -1)}
                    >
                      <IconArrowUp size={14} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      aria-label="Move down"
                      disabled={!editable || i === fields.length - 1}
                      onClick={() => move(i, 1)}
                    >
                      <IconArrowDown size={14} />
                    </ActionIcon>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      aria-label="Remove field"
                      disabled={!editable}
                      onClick={() => setFields(fields.filter((_, j) => j !== i))}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Stack>
              </Group>
              {supporting.length > 0 && (
                <MultiSelect
                  label="Uses inputs"
                  description="The tables the AI may use for this column. The Must match table is always included."
                  placeholder={f.inputs.length ? undefined : "None — the document only"}
                  data={tableOptions}
                  value={f.inputs.map(String)}
                  disabled={!editable}
                  onChange={(v) => {
                    const picked = new Set(v.map(Number));
                    if (f.matchInput) picked.add(f.matchInput);
                    setField(i, { inputs: [...picked].sort((a, b) => a - b) });
                  }}
                />
              )}
              <Textarea
                label="Prompt"
                placeholder={
                  supporting.length > 0
                    ? "What should the AI predict? e.g. Pick the document type from Input 1, based on the title page."
                    : "What should the AI predict for this column?"
                }
                value={f.prompt}
                disabled={!editable}
                autosize
                minRows={2}
                maxLength={1000}
                error={f.prompt.trim() ? null : "Say what to predict"}
                onChange={(e) => setField(i, { prompt: e.currentTarget.value })}
              />
              <TextInput
                label="Examples"
                description="Optional — comma or line separated. They show the form, not the answer."
                placeholder="Optional"
                value={f.examples}
                disabled={!editable}
                onChange={(e) => setField(i, { examples: e.currentTarget.value })}
              />
              {supporting.length > 0 && (
                <Group gap={4} wrap="nowrap" align="flex-start" grow>
                  <Select
                    label="Must match"
                    description="Value must come from a table"
                    data={inputOptions}
                    value={String(f.matchInput > supporting.length ? 0 : f.matchInput)}
                    allowDeselect={false}
                    disabled={!editable}
                    onChange={(v) => {
                      const matchInput = Number(v) || 0;
                      const inputs =
                        matchInput && !f.inputs.includes(matchInput)
                          ? [...f.inputs, matchInput].sort((a, b) => a - b)
                          : f.inputs;
                      setField(i, { matchInput, matchColumn: "", inputs });
                    }}
                  />
                  {f.matchInput > 0 && f.matchInput <= supporting.length && (
                    <Select
                      label="Column"
                      description=" "
                      placeholder="Pick a column"
                      data={inputColumns[`in-${f.matchInput}`] ?? []}
                      value={f.matchColumn || null}
                      searchable
                      disabled={!editable}
                      error={matchError(f, supporting.length)}
                      nothingFoundMessage="Connect the table first"
                      onChange={(v) => setField(i, { matchColumn: v ?? "" })}
                    />
                  )}
                </Group>
              )}
              {f.type === "picklist" && (
                <Textarea
                  label="Allowed values"
                  description="The model must choose one of these (one per line or comma separated), or answer not found."
                  value={f.values}
                  disabled={!editable}
                  autosize
                  minRows={2}
                  error={f.values.trim() ? null : "A picklist needs its values"}
                  onChange={(e) => setField(i, { values: e.currentTarget.value })}
                />
              )}
              <Checkbox
                label="Required — a row missing it is marked partial"
                checked={f.required}
                disabled={!editable}
                onChange={(e) => setField(i, { required: e.currentTarget.checked })}
              />
            </Stack>
          </Paper>
        );
      })}
      <Button
        variant="light"
        size="xs"
        leftSection={<IconPlus size={14} />}
        disabled={!editable || fields.length >= 40}
        onClick={() =>
          setFields([
            ...fields,
            {
              name: "",
              prompt: "",
              examples: "",
              type: "text",
              values: "",
              required: false,
              matchInput: 0,
              matchColumn: "",
              inputs: allInputs(supporting.length),
            },
          ])
        }
      >
        Add field
      </Button>

      <ChatModelPicker
        options={options}
        error={error}
        platform={platform}
        value={choice}
        disabled={!editable}
        footnote="One call per document. Every call is recorded with its model and version."
        onChange={(next) => {
          onChange("connectionId", next.connectionId ?? "");
          onChange("deployment", next.deployment ?? "");
        }}
      />

      <Group grow>
        <NumberInput
          label="Max documents"
          min={1}
          max={1000}
          value={Number(config.maxDocuments ?? 200)}
          disabled={!editable}
          onChange={(v) => onChange("maxDocuments", Number(v) || 200)}
        />
        <NumberInput
          label="Concurrency"
          min={1}
          max={8}
          value={Number(config.concurrency ?? 4)}
          disabled={!editable}
          onChange={(v) => onChange("concurrency", Number(v) || 4)}
        />
      </Group>
      <NumberInput
        label="Max characters of text sent"
        description="Longer documents send their start and end."
        min={1000}
        max={100000}
        step={1000}
        thousandSeparator=","
        value={Number(config.maxChars ?? 20000)}
        disabled={!editable}
        onChange={(v) => onChange("maxChars", Number(v) || 20000)}
      />
      {supporting.length > 0 && (
        <NumberInput
          label="Max characters of supporting tables sent"
          description="Shared by all supporting inputs. Rows past the limit aren't sent to the AI, but every row is still used to check values."
          min={1000}
          max={100000}
          step={1000}
          thousandSeparator=","
          value={Number(config.maxReferenceChars ?? 30000)}
          disabled={!editable}
          onChange={(v) => onChange("maxReferenceChars", Number(v) || 30000)}
        />
      )}

      <Button
        size="xs"
        variant="light"
        leftSection={<IconPlayerPlay size={14} />}
        loading={testing}
        disabled={invalid}
        onClick={() => void test()}
      >
        Test — predict the first 3 documents
      </Button>

      {preview?.error && (
        <Alert color="red" variant="light" title="Test failed">
          {preview.error}
        </Alert>
      )}
      {preview && !preview.error && (
        <ScrollArea type="auto">
          <Table withTableBorder withColumnBorders fz="xs" style={{ minWidth: 420 }}>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Document</Table.Th>
                {names.map((n) => (
                  <Table.Th key={n} style={{ fontFamily: "var(--mantine-font-family-monospace)" }}>
                    {n}
                  </Table.Th>
                ))}
                <Table.Th>Status</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {preview.rows.length === 0 && (
                <Table.Tr>
                  <Table.Td colSpan={names.length + 2}>
                    <Text size="xs" c="dimmed">
                      No documents upstream.
                    </Text>
                  </Table.Td>
                </Table.Tr>
              )}
              {preview.rows.map((row, i) => {
                const status = String(row._prediction_status ?? "");
                return (
                  <Table.Tr key={i}>
                    <Table.Td>{documentLabel(row)}</Table.Td>
                    {names.map((n) => (
                      <Table.Td key={n} title={cell(row[`${n}__evidence`])}>
                        <Stack gap={2}>
                          <Text size="xs">{cell(row[n])}</Text>
                          {row[n] !== null && row[n] !== undefined && (
                            <Text size="xs" c="dimmed">
                              {Math.round(Number(row[`${n}__confidence`] ?? 0) * 100)}%
                            </Text>
                          )}
                        </Stack>
                      </Table.Td>
                    ))}
                    <Table.Td>
                      <Stack gap={2}>
                        <Badge
                          size="sm"
                          variant="light"
                          color={STATUS_COLOR[status] ?? "gray"}
                          styles={BADGE_STYLES}
                        >
                          {status || "—"}
                        </Badge>
                        {row._prediction_note ? (
                          <Text size="xs" c="dimmed">
                            {String(row._prediction_note)}
                          </Text>
                        ) : null}
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                );
              })}
            </Table.Tbody>
          </Table>
        </ScrollArea>
      )}
    </Stack>
  );
}
